import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Zap, Flame, Leaf, TrendingDown, BadgeCheck, ArrowRight, Sparkles } from "lucide-react";
import { TRV_BASE_PRICE_EUR_KWH, TRV_BASE_SUBSCRIPTION_EUR_MONTH } from "@/config/regulatedReference";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BillUploader from "@/components/BillUploader";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

export default function Comparator() {
    const c = useSiteContent(keysForMany("common", "comparator"));
    const [params] = useSearchParams();
    const audienceParam = (params.get("audience") || "").toLowerCase();
    // Si un param audience est présent dans l'URL → on verrouille le toggle
    // (le visiteur arrive d'un parcours dédié, pas de switch accidentel)
    const audienceLocked = ["particulier", "b2c", "entreprise", "b2b", "pro"].includes(audienceParam);
    const initialAudience =
        audienceParam === "b2b" || audienceParam === "entreprise" || audienceParam === "pro"
            ? "b2b"
            : "b2c";
    const initialEnergy = params.get("type") === "gas" ? "gas" : "electricity";
    const initialKwh = Number(params.get("kwh")) || 4500;
    // Défaut = tarif réglementé officiel, pas une vieille valeur figée.
    const initialPrice = Number(params.get("price")) || TRV_BASE_PRICE_EUR_KWH;
    const initialSub = Number(params.get("sub")) || TRV_BASE_SUBSCRIPTION_EUR_MONTH;

    const [audience, setAudience] = useState(initialAudience);
    const [energyType, setEnergyType] = useState(initialEnergy);
    const [annualKwh, setAnnualKwh] = useState(initialKwh);
    const [pricePerKwh, setPricePerKwh] = useState(initialPrice);
    const [subscription, setSubscription] = useState(initialSub);
    const [greenOnly, setGreenOnly] = useState(false);

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const runSim = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/comparator/simulate", {
                annual_kwh: Number(annualKwh),
                current_price_kwh: Number(pricePerKwh),
                current_subscription_monthly: Number(subscription),
                energy_type: energyType,
                audience,
                green_only: greenOnly,
            });
            setResult(data);
            toast.success(`${data.offers.length} offres trouvées`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="em-container py-16" data-testid="comparator-page">
            <p className="em-eyebrow">{c("comparator.hero.eyebrow", "Comparateur d'offres")}</p>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-primary mt-2">
                {c("comparator.hero.title", "Trouvez l'offre qui vous va.")}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-2xl text-lg">
                {c("comparator.hero.subtitle", "Simulez votre consommation et comparez instantanément les offres des fournisseurs partenaires. Sans engagement, sans email obligatoire.")}
            </p>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
                {/* SIDEBAR FORM */}
                <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
                    <BillUploader
                        onApply={(d) => {
                            if (d.annual_kwh) setAnnualKwh(d.annual_kwh);
                            if (d.current_price_kwh) setPricePerKwh(d.current_price_kwh);
                            if (d.current_subscription_monthly) setSubscription(d.current_subscription_monthly);
                            if (d.energy_type) setEnergyType(d.energy_type);
                        }}
                    />
                    <Card className="rounded-3xl border-border">
                    <CardContent className="p-7 space-y-6">
                        {audienceLocked ? (
                            // Audience verrouillée par l'URL — on cache le toggle pour garder
                            // le visiteur dans son parcours d'origine sans switch accidentel.
                            <div className="rounded-full bg-primary/5 border border-primary/15 px-5 py-3 inline-flex items-center gap-2">
                                <span className="text-sm">👤</span>
                                <span className="font-semibold text-primary">
                                    {audience === "b2b"
                                        ? c("comparator.locked.b2b", "Je suis une entreprise")
                                        : c("comparator.locked.b2c", "Je suis un particulier")}
                                </span>
                            </div>
                        ) : (
                            <div>
                                <Label className="em-eyebrow">{c("comparator.form.label_audience", "Audience")}</Label>
                                <Tabs value={audience} onValueChange={setAudience} className="mt-2">
                                    <TabsList className="w-full rounded-full">
                                        <TabsTrigger value="b2c" className="flex-1 rounded-full" data-testid="comparator-tab-b2c">{c("common.audience.b2c", "Particulier")}</TabsTrigger>
                                        <TabsTrigger value="b2b" className="flex-1 rounded-full" data-testid="comparator-tab-b2b">{c("common.audience.b2b", "Entreprise")}</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                        )}

                        <div>
                            <Label className="em-eyebrow">{c("comparator.form.label_energy", "Type d'énergie")}</Label>
                            <Tabs value={energyType} onValueChange={setEnergyType} className="mt-2">
                                <TabsList className="w-full rounded-full">
                                    <TabsTrigger value="electricity" className="flex-1 rounded-full gap-2" data-testid="comparator-tab-electricity">
                                        <Zap className="h-4 w-4" /> {c("common.energy.elec", "Électricité")}
                                    </TabsTrigger>
                                    <TabsTrigger value="gas" className="flex-1 rounded-full gap-2" data-testid="comparator-tab-gas">
                                        <Flame className="h-4 w-4" /> {c("common.energy.gas", "Gaz")}
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>

                        <div>
                            <Label htmlFor="kwh" className="em-eyebrow">{c("comparator.form.label_kwh", "Consommation annuelle (kWh)")}</Label>
                            <Input
                                id="kwh"
                                type="number"
                                value={annualKwh}
                                onChange={(e) => setAnnualKwh(e.target.value)}
                                className="rounded-xl mt-2"
                                data-testid="comparator-input-kwh"
                            />
                        </div>

                        <div>
                            <Label htmlFor="price" className="em-eyebrow">{c("comparator.form.label_price", "Prix actuel du kWh (€)")}</Label>
                            <Input
                                id="price"
                                type="number"
                                step="0.0001"
                                value={pricePerKwh}
                                onChange={(e) => setPricePerKwh(e.target.value)}
                                className="rounded-xl mt-2"
                                data-testid="comparator-input-price"
                            />
                        </div>

                        <div>
                            <Label htmlFor="sub" className="em-eyebrow">{c("comparator.form.label_subscription", "Abonnement actuel (€/mois)")}</Label>
                            <Input
                                id="sub"
                                type="number"
                                step="0.01"
                                value={subscription}
                                onChange={(e) => setSubscription(e.target.value)}
                                className="rounded-xl mt-2"
                                data-testid="comparator-input-sub"
                            />
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-border">
                            <div className="flex items-center gap-2">
                                <Leaf className="h-4 w-4 text-primary" />
                                <Label htmlFor="green" className="text-sm font-semibold">{c("common.energy.green", "Énergie verte uniquement")}</Label>
                            </div>
                            <Switch id="green" checked={greenOnly} onCheckedChange={setGreenOnly} data-testid="comparator-green-switch" />
                        </div>

                        <Button onClick={runSim} disabled={loading} size="lg" className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="comparator-submit-btn">
                            {loading ? "Calcul…" : c("comparator.form.submit", "Comparer maintenant")}
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </CardContent>
                </Card>
                </div>

                {/* RESULTS */}
                <div className="space-y-4">
                    {result && (
                        <Card className="rounded-3xl bg-primary text-primary-foreground border-0">
                            <CardContent className="p-7 grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-primary-foreground/70">{c("comparator.result.label_current", "Coût actuel")}</p>
                                    <p className="font-display text-3xl font-black mt-1" data-testid="result-current-cost">{result.current_annual_cost.toFixed(0)} €</p>
                                    <p className="text-sm text-primary-foreground/70">par an</p>
                                </div>
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-primary-foreground/70">{c("comparator.result.label_best", "Meilleure offre")}</p>
                                    <p className="font-display text-3xl font-black mt-1" data-testid="result-best-cost">{result.best_annual_cost.toFixed(0)} €</p>
                                    <p className="text-sm text-primary-foreground/70">par an</p>
                                </div>
                                {/* Le bloc "Économies" n'apparaît que s'il y en a vraiment une.
                                    Sinon on dit ce qui est : soit le client surpaie face au tarif
                                    réglementé, soit son contrat est déjà compétitif. */}
                                {result.best_savings_eur > 0 ? (
                                    <div className="bg-accent rounded-2xl p-4 -m-1 md:-m-2 text-accent-foreground">
                                        <p className="text-xs tracking-[0.2em] uppercase text-accent-foreground/70">{c("comparator.result.label_savings", "Économies")}</p>
                                        <p className="font-display text-3xl font-black mt-1 flex items-baseline gap-1" data-testid="result-savings">
                                            <TrendingDown className="h-5 w-5" />
                                            {result.best_savings_eur.toFixed(0)} €
                                        </p>
                                        <p className="text-sm text-accent-foreground/70">soit {result.best_savings_pct.toFixed(1)}%</p>
                                    </div>
                                ) : result.regulated_savings_eur > 0 ? (
                                    <div className="bg-white rounded-2xl p-4 -m-1 md:-m-2 text-destructive" data-testid="result-overpaying">
                                        <p className="text-xs tracking-[0.2em] uppercase opacity-70">Alerte</p>
                                        <p className="font-display text-2xl font-black mt-1" data-testid="result-savings">
                                            +{result.regulated_savings_eur.toFixed(0)} € / an
                                        </p>
                                        <p className="text-sm mt-1 leading-snug">
                                            Vous payez plus cher que le tarif réglementé. Y revenir vous
                                            ferait économiser ce montant.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="bg-white/10 rounded-2xl p-4 -m-1 md:-m-2" data-testid="result-competitive">
                                        <p className="text-xs tracking-[0.2em] uppercase text-primary-foreground/70">Économies</p>
                                        <p className="font-display text-3xl font-black mt-1" data-testid="result-savings">0 €</p>
                                        <p className="text-sm text-primary-foreground/70 leading-snug">
                                            Votre contrat reste compétitif. Nous continuons la veille.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Périmètre comparé — obligatoire dès qu'un classement est affiché. */}
                    {result && (
                        <div className="rounded-2xl border border-border bg-secondary/50 p-4 space-y-2" data-testid="result-scope">
                            {result.catalog_source === "demo_seed" && (
                                <p className="text-sm font-semibold text-destructive flex items-start gap-2" data-testid="result-demo-warning">
                                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                    Données de démonstration : ces offres ne constituent pas une recommandation.
                                </p>
                            )}
                            {result.regulated_reference_annual_eur != null && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Tarif réglementé (repère indépendant)</span>
                                    <span className="font-mono font-bold text-primary" data-testid="result-regulated-ref">
                                        {result.regulated_reference_annual_eur.toFixed(0)} € / an
                                    </span>
                                </div>
                            )}
                            {result.scope_notice && (
                                <p className="text-xs text-muted-foreground leading-relaxed">{result.scope_notice}</p>
                            )}
                        </div>
                    )}

                    {!result && !loading && (
                        <div className="bg-secondary rounded-3xl p-12 text-center border-2 border-dashed border-border" data-testid="comparator-empty">
                            <Sparkles className="h-10 w-10 mx-auto text-primary" />
                            <p className="font-display text-2xl font-bold text-primary mt-4">{c("comparator.empty.title", "Renseignez vos infos puis lancez la comparaison")}</p>
                            <p className="text-muted-foreground mt-2">{c("comparator.empty.body", "Nous comparons nos offres partenaires et vous situons face au tarif réglementé.")}</p>
                        </div>
                    )}

                    {loading && (
                        <div className="space-y-3" data-testid="comparator-skeleton">
                            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}
                        </div>
                    )}

                    {result && result.offers.length > 0 && (
                        <div className="space-y-3" data-testid="comparator-results">
                            {result.offers.map((offer, idx) => (
                                <Card key={offer.id} className="rounded-2xl border-border em-card-hover" data-testid={`offer-${offer.id}`}>
                                    <CardContent className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                                        <div className="md:col-span-1">
                                            <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-display font-black text-lg ${idx === 0 ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"}`}>
                                                {idx + 1}
                                            </div>
                                        </div>
                                        <div className="md:col-span-4">
                                            <p className="font-display font-bold text-lg text-primary">{offer.supplier_name}</p>
                                            <p className="text-sm text-muted-foreground">{offer.name}</p>
                                            <div className="flex gap-2 mt-2">
                                                {offer.green && <Badge variant="outline" className="border-primary text-primary"><Leaf className="h-3 w-3 mr-1" />Verte</Badge>}
                                                {offer.fixed_price && <Badge variant="outline">Prix fixe</Badge>}
                                            </div>
                                        </div>
                                        <div className="md:col-span-3">
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Prix kWh</p>
                                            <p className="font-mono font-bold text-primary text-lg">{offer.price_per_kwh.toFixed(4)} €</p>
                                            <p className="text-xs text-muted-foreground">Abo. {offer.subscription_monthly.toFixed(2)} €/mois</p>
                                        </div>
                                        <div className="md:col-span-2 text-right md:text-left">
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Coût annuel</p>
                                            <p className="font-display font-bold text-xl">{offer.annual_cost_eur.toFixed(0)} €</p>
                                        </div>
                                        <div className="md:col-span-2 text-right">
                                            {offer.annual_savings_eur > 0 ? (
                                                <>
                                                    <p className="text-xs uppercase tracking-wider text-primary">Économie</p>
                                                    <p className="font-display font-black text-xl text-primary flex items-center justify-end gap-1">
                                                        <BadgeCheck className="h-5 w-5" />
                                                        {offer.annual_savings_eur.toFixed(0)} €
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">{offer.annual_savings_pct.toFixed(1)}%</p>
                                                </>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">+{Math.abs(offer.annual_savings_eur).toFixed(0)} €</p>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
